import React, { useState } from "react";
import { appointmentRepo, assetRepo, resourceRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Card, EmptyState, PageHead, StatusBadge, formatDate, useAsync } from "../../components/ui";

const HOURS = Array.from({ length: 24 }).map((_, i) => i);

const CELL = 46; // กว้างช่องละชั่วโมง (px) ต้องตรงกับ CSS .gantt__cell

const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

// แปลงนัดหมายจริงเป็น block บนไทม์ไลน์ (เริ่มกี่โมง + กี่นาที)
const blockOf = (apt) => {
  const start = new Date(apt.startAt);
  const startMin = start.getHours() * 60 + start.getMinutes();
  const duration = Number(apt.durationMin) || 60;
  return { hour: start.getHours(), left: (start.getMinutes() / 60) * CELL + 2, width: Math.max(((Math.min(duration, 1440 - startMin)) / 60) * CELL - 4, 12), apt };
};

export default function ResourceTimeline() {
  const { orgId, branchId, hasModule } = useApp();
  const { tx } = useI18n();
  const useFleet = hasModule("fleet");
  const { data: resources } = useAsync(() => resourceRepo.all(orgId, (row) => !branchId || row.branchId === branchId), [orgId, branchId], []);
  const { data: assets } = useAsync(() => (useFleet ? assetRepo.all(orgId, (row) => !branchId || row.branchId === branchId) : Promise.resolve([])), [orgId, branchId, useFleet], []);

  const [day, setDay] = useState(() => new Date());
  const shiftDay = (n) => setDay((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
  // นัดหมายจริงของวันที่เลือก (ตัดที่ยกเลิก/ไม่มาออก)
  const { data: appointments } = useAsync(
    () => (useFleet ? Promise.resolve([]) : appointmentRepo.all(orgId, (row) => row.resourceId && !["cancelled", "noshow"].includes(row.status) && sameDay(row.startAt, day))),
    [orgId, useFleet, day],
    []
  );

  const rows = useFleet ? assets : resources;

  return (
    <>
      <PageHead title={tx("ไทม์ไลน์ทรัพยากร", "Resource timeline")} subtitle={tx("นัดหมายจริงของแต่ละทรัพยากรรายชั่วโมง", "Real appointments per resource by the hour")}
        actions={
          !useFleet ? (
            <>
              <Button size="sm" onClick={() => shiftDay(-1)}>←</Button>
              <span className="u-strong">{formatDate(day)}</span>
              <Button size="sm" onClick={() => shiftDay(1)}>→</Button>
              <Button size="sm" onClick={() => setDay(new Date())}>{tx("วันนี้", "Today")}</Button>
            </>
          ) : null
        }
      />

      <Card>
        {useFleet && rows?.length ? (
          <div className="u-fs-13 u-muted u-mb-8">{tx("ยานพาหนะ/สินทรัพย์ยังไม่มีข้อมูลเวลาใช้งาน แสดงเฉพาะสถานะปัจจุบัน", "Assets have no booking times recorded — showing current status only")}</div>
        ) : null}
        {!useFleet && rows?.length && !appointments?.length ? (
          <div className="u-fs-13 u-muted u-mb-8">{tx("วันนี้ไม่มีนัดหมายที่ระบุทรัพยากร", "No appointments assigned to a resource on this day")}</div>
        ) : null}
        {!rows?.length ? (
          <EmptyState text={tx("ยังไม่มีทรัพยากร", "No resources")} />
        ) : (
          <div className="gantt">
            <div className="gantt__row gantt__row--head">
              <div className="gantt__label">{tx("ทรัพยากร", "Resource")}</div>
              {HOURS.map((hour) => (
                <div key={hour} className="gantt__cell">{hour}:00</div>
              ))}
            </div>
            {rows.map((row) => {
              const blocks = useFleet ? [] : appointments.filter((apt) => apt.resourceId === row.id).map(blockOf);
              return (
                <div className="gantt__row" key={row.id}>
                  <div className="gantt__label">
                    {row.name}
                    <div style={{ marginTop: 4 }}><StatusBadge status={row.status} /></div>
                  </div>
                  {HOURS.map((hour) => (
                    <div key={hour} className="gantt__cell">
                      {blocks
                        .filter((block) => block.hour === hour)
                        .map((block) => (
                          <div
                            key={block.apt.id}
                            className="gantt__bar is-busy"
                            title={`${block.apt.customerName || ""} · ${block.apt.serviceName || ""}`}
                            style={{ left: block.left, width: block.width, overflow: "hidden", whiteSpace: "nowrap" }}
                          >
                            {block.apt.customerName || tx("ไม่ว่าง", "Busy")}
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}
